// components/CreateProjectModal.jsx
import React, { useState, useEffect } from "react";
import { X, Building2, ChevronRight, CheckSquare } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateProjectModal = ({ account, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [selectedTaskTemplates, setSelectedTaskTemplates] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const [formData, setFormData] = useState({
    projectName: "",
    projectNumber: "",
    accountId: account?.id || "",
    description: "",
    status: "Survey Requested",
    priority: "Medium",
    type: "",
    projectManagerId: "",
    startDate: "",
    endDate: "",
    installationDate: "",
    surveyDate: "",
    clientSurveyContactId: "",
    surveyCompletedById: "",
    surveyCompletedDate: "",
    surveyFileSaved: false,
    financeApproved: false,
    financeCheckSubmittedDate: "",
    hardwareOrdered: false,
    kitListCreated: false,
    paperworkCheckedAndSaved: false,
    installsPaperworkComplete: false,
    broadbandInfoUpdated: "",
    broadbandInstalledDate: "",
    broadbandNotRequired: false,
    broadbandRequestDate: "",
    installationCompletedDate: "",
    installDate: "",
    installCompletedDateDEP: "",
    portAcceptedDate: "",
    portRequestDate: "",
    projectBlocked: false,
    projectBlockedReason: "",
    serviceActivityComplete: false,
    serviceCheckerComplete: false,
    stageTwoInstallDate: "",
    systemBuilt: false,
    systemOrderedDate: "",
    techInfoCompleted: false,
    projectTemplateId: "",
    projectNotes: "",
  });

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
    fetchCurrentUser();
    fetchProjectTemplates();
    if (account?.id) {
      fetchContacts(account.id);
    }
  }, []);

  useEffect(() => {
    if (formData.accountId && formData.accountId !== account?.id) {
      fetchContacts(formData.accountId);
    }
  }, [formData.accountId]);

  useEffect(() => {
    if (formData.projectTemplateId) {
      fetchTaskTemplates(formData.projectTemplateId);
    } else {
      setTaskTemplates([]);
      setSelectedTaskTemplates([]);
    }
  }, [formData.projectTemplateId]);

  const fetchAccounts = async () => {
    try {
      const response = await apiService.getAccounts({ limit: 1000 });
      setAccounts(response.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.users || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await apiService.getMe();
      setCurrentUser(response.user);
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  };

  const fetchContacts = async (accountId) => {
    try {
      const response = await apiService.getContacts({ accountId });
      setContacts(response.data || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
    }
  };

  const fetchProjectTemplates = async () => {
    try {
      const response = await apiService.getProjectTemplates();
      setProjectTemplates(response.data || []);
    } catch (err) {
      console.error("Error fetching project templates:", err);
    }
  };

  const fetchTaskTemplates = async (projectTemplateId) => {
    try {
      const response = await apiService.getTaskTemplates(projectTemplateId);
      setTaskTemplates(response.data || []);
      setSelectedTaskTemplates(response.data.map((t) => t.id));
    } catch (err) {
      console.error("Error fetching task templates:", err);
    }
  };

  const handleAccountChange = (accountId) => {
    setFormData({
      ...formData,
      accountId,
      clientSurveyContactId: "",
    });
    if (accountId) {
      fetchContacts(accountId);
    } else {
      setContacts([]);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.projectName || !formData.accountId) {
        setError("Please fill in required fields");
        return;
      }
      setError("");
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setError("");
    setCurrentStep(1);
  };

  const toggleTaskTemplate = (taskTemplateId) => {
    setSelectedTaskTemplates((prev) =>
      prev.includes(taskTemplateId)
        ? prev.filter((id) => id !== taskTemplateId)
        : [...prev, taskTemplateId],
    );
  };

  const toggleAllTaskTemplates = () => {
    if (selectedTaskTemplates.length === taskTemplates.length) {
      setSelectedTaskTemplates([]);
    } else {
      setSelectedTaskTemplates(taskTemplates.map((t) => t.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = {
        ...formData,
        ownerId: currentUser?.id,
      };

      const cleanUuidField = (value) => {
        return value && value.trim() !== "" ? value : null;
      };

      submitData.projectManagerId = cleanUuidField(submitData.projectManagerId);
      submitData.clientSurveyContactId = cleanUuidField(
        submitData.clientSurveyContactId,
      );
      submitData.surveyCompletedById = cleanUuidField(
        submitData.surveyCompletedById,
      );
      submitData.projectTemplateId = cleanUuidField(
        submitData.projectTemplateId,
      );

      Object.keys(submitData).forEach((key) => {
        if (key.includes("Date") && submitData[key] === "") {
          submitData[key] = null;
        }
      });

      if (!submitData.description?.trim()) submitData.description = null;
      if (!submitData.type?.trim()) submitData.type = null;
      if (!submitData.broadbandInfoUpdated?.trim())
        submitData.broadbandInfoUpdated = null;
      if (!submitData.projectBlockedReason?.trim())
        submitData.projectBlockedReason = null;
      if (!submitData.projectNotes?.trim()) submitData.projectNotes = null;
      if (!submitData.projectNumber?.trim()) submitData.projectNumber = null;

      submitData.selectedTaskTemplates = selectedTaskTemplates;

      await apiService.createProjectWithTasks(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating project:", err);
      setError(err.response?.data?.message || "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-4">
            <h2
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              New Project
            </h2>
            <div className="flex items-center gap-2 text-sm">
              <span
                className={`px-3 py-1 rounded ${
                  currentStep === 1
                    ? "bg-blue-600 text-white"
                    : isDarkMode
                      ? "bg-gray-700 text-gray-400"
                      : "bg-gray-200 text-gray-600"
                }`}
              >
                1. Project Details
              </span>
              <ChevronRight
                size={16}
                className={isDarkMode ? "text-gray-600" : "text-gray-400"}
              />
              <span
                className={`px-3 py-1 rounded ${
                  currentStep === 2
                    ? "bg-blue-600 text-white"
                    : isDarkMode
                      ? "bg-gray-700 text-gray-400"
                      : "bg-gray-200 text-gray-600"
                }`}
              >
                2. Select Tasks
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/20 border border-red-500/30 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          {currentStep === 1 ? (
            <form id="projectForm">
              {/* PROJECT INFORMATION */}
              <div
                className={`px-4 py-2 mb-4 rounded ${
                  isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                }`}
              >
                <h3
                  className={`text-sm font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Project Information
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-6">
                {/* Project Name */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <span className="text-red-500">*</span> Project Name
                  </label>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={formData.projectName}
                      onChange={(e) =>
                        handleChange("projectName", e.target.value)
                      }
                      required
                      placeholder="ABMA EDUCA / Install Type 1 / 12_2025"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                      }`}
                    />
                  </div>
                </div>

                {/* Account */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <span className="text-red-500">*</span> Account
                  </label>
                  <div className="col-span-2">
                    {account ? (
                      <div
                        className={`flex items-center gap-2 px-3 py-2 border rounded ${
                          isDarkMode
                            ? "bg-blue-500/20 border-blue-500/30"
                            : "bg-blue-50 border-blue-200"
                        }`}
                      >
                        <Building2
                          className={`w-4 h-4 ${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                        />
                        <span
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {account.name}
                        </span>
                      </div>
                    ) : (
                      <select
                        value={formData.accountId}
                        onChange={(e) => handleAccountChange(e.target.value)}
                        required
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">Select Account</option>
                        {accounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Project Number */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Project Number
                  </label>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={formData.projectNumber}
                      onChange={(e) =>
                        handleChange("projectNumber", e.target.value)
                      }
                      placeholder="Auto-generated if blank"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                      }`}
                    />
                  </div>
                </div>

                {/* Owner (Read-only) */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Owner
                  </label>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={currentUser?.name || "Loading..."}
                      disabled
                      className={`w-full px-3 py-2 border rounded cursor-not-allowed ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-gray-400"
                          : "bg-gray-100 border-gray-300 text-gray-500"
                      }`}
                    />
                    <p
                      className={`mt-1 text-xs ${
                        isDarkMode ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      You will be set as the owner of this project
                    </p>
                  </div>
                </div>

                {/* Project Template */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Project Template
                  </label>
                  <div className="col-span-2">
                    <select
                      value={formData.projectTemplateId}
                      onChange={(e) =>
                        handleChange("projectTemplateId", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">--None--</option>
                      {projectTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.projectTemplateName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Project Manager */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Project Manager
                  </label>
                  <div className="col-span-2">
                    <select
                      value={formData.projectManagerId}
                      onChange={(e) =>
                        handleChange("projectManagerId", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">--None--</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Status */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Status
                  </label>
                  <div className="col-span-2">
                    <select
                      value={formData.status}
                      onChange={(e) => handleChange("status", e.target.value)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="Survey Requested">Survey Requested</option>
                      <option value="Awaiting Survey">Awaiting Survey</option>
                      <option value="Survey Booked">Survey Booked</option>
                      <option value="Survey Completed">Survey Completed</option>
                      <option value="Quotation">Quotation</option>
                      <option value="Installation">Installation</option>
                      <option value="Awaiting Finance Approval">
                        Awaiting Finance Approval
                      </option>
                      <option value="Porting">Porting</option>
                      <option value="Broadband Install">
                        Broadband Install
                      </option>
                      <option value="Complete">Complete</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {/* Priority */}
                <div className="grid grid-cols-3 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Priority
                  </label>
                  <div className="col-span-2">
                    <select
                      value={formData.priority}
                      onChange={(e) => handleChange("priority", e.target.value)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Description - Full Width */}
                <div className="col-span-2 grid grid-cols-6 gap-4 items-start">
                  <label
                    className={`text-sm pt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Description
                  </label>
                  <div className="col-span-5">
                    <textarea
                      rows="3"
                      value={formData.description}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                      placeholder="Project description..."
                      className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                      }`}
                    />
                  </div>
                </div>
              </div>
            </form>
          ) : (
            // STEP 2: SELECT TASK TEMPLATES
            <div className="space-y-4">
              <div
                className={`border rounded-lg p-4 ${
                  isDarkMode
                    ? "bg-blue-500/10 border-blue-500/30"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <h3
                  className={`font-semibold mb-2 ${
                    isDarkMode ? "text-blue-400" : "text-blue-700"
                  }`}
                >
                  Select Tasks to Create
                </h3>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Choose which tasks from the template should be created with
                  this project. You can deselect tasks that aren't needed.
                </p>
              </div>

              {taskTemplates.length === 0 ? (
                <div className="text-center py-12">
                  <CheckSquare
                    size={48}
                    className={`mx-auto mb-4 ${
                      isDarkMode ? "text-gray-600" : "text-gray-400"
                    }`}
                  />
                  <p
                    className={`mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    No task templates found for this project template
                  </p>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    The project will be created without predefined tasks
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Select All Toggle */}
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700"
                        : "bg-gray-100 border-gray-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={
                        selectedTaskTemplates.length === taskTemplates.length
                      }
                      onChange={toggleAllTaskTemplates}
                      className={`w-4 h-4 rounded border text-blue-600 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12]"
                          : "border-gray-300 bg-white"
                      }`}
                    />
                    <span
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Select All ({taskTemplates.length} tasks)
                    </span>
                  </div>

                  {/* Task Template List */}
                  {taskTemplates.map((taskTemplate, index) => (
                    <div
                      key={taskTemplate.id}
                      className={`flex items-start gap-3 p-4 rounded-lg transition-colors cursor-pointer ${
                        isDarkMode
                          ? "bg-gray-800/30 hover:bg-gray-800/50"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleTaskTemplate(taskTemplate.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTaskTemplates.includes(
                          taskTemplate.id,
                        )}
                        onChange={() => toggleTaskTemplate(taskTemplate.id)}
                        className={`w-4 h-4 rounded border text-blue-600 mt-1 ${
                          isDarkMode
                            ? "border-gray-700 bg-[#0C0E12]"
                            : "border-gray-300 bg-white"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span
                            className={`text-sm font-medium ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            #{index + 1}
                          </span>
                          <span
                            className={`font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {taskTemplate.taskTemplateName}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              taskTemplate.defaultPriority === "Urgent"
                                ? "bg-red-500/20 text-red-400"
                                : taskTemplate.defaultPriority === "High"
                                  ? "bg-orange-500/20 text-orange-400"
                                  : taskTemplate.defaultPriority === "Medium"
                                    ? "bg-yellow-500/20 text-yellow-400"
                                    : "bg-blue-500/20 text-blue-400"
                            }`}
                          >
                            {taskTemplate.defaultPriority}
                          </span>
                        </div>

                        {taskTemplate.templateDescription && (
                          <p
                            className={`text-sm mb-2 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {taskTemplate.templateDescription}
                          </p>
                        )}

                        <div
                          className={`flex items-center gap-4 text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          {taskTemplate.defaultAssignee && (
                            <span>
                              Assigned to: {taskTemplate.defaultAssignee.name}
                            </span>
                          )}
                          {taskTemplate.defaultDueDate && (
                            <>
                              <span>•</span>
                              <span>
                                Due: +{taskTemplate.defaultDueDate} days
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div
                className={`rounded-lg p-4 mt-4 ${
                  isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                }`}
              >
                <div
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <strong
                    className={isDarkMode ? "text-white" : "text-gray-900"}
                  >
                    {selectedTaskTemplates.length}
                  </strong>{" "}
                  of {taskTemplates.length} tasks will be created
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-between ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div>
            {currentStep === 2 && (
              <button
                type="button"
                onClick={handleBack}
                className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
                  isDarkMode
                    ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                    : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                Back
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
                isDarkMode
                  ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                  : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
              }`}
            >
              Cancel
            </button>

            {currentStep === 1 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={
                  !formData.projectName || !formData.accountId || !currentUser
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Select Tasks
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !currentUser}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? "Creating..."
                  : `Create Project${
                      selectedTaskTemplates.length > 0
                        ? ` with ${selectedTaskTemplates.length} Tasks`
                        : ""
                    }`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
